import type { TeleconsultManagementVisit } from '../../types/api';
import {
  DatePreset,
  eachIso,
  inclusiveDaySpan,
  parseIso,
} from './managementRange';

/** Aspen-friendly green for completed stack segments (theme success). */
export const COMPLETED_BAR_COLOR = '#2e7d32';

/** Short ranges stay daily; long spans (year / custom > 45d) roll up by month. */
export const MONTHLY_AGGREGATION_DAY_THRESHOLD = 45;

/**
 * Daily charts wider than this use day-of-month axis ticks (1, 5, 10…) instead of
 * "1 Sept" — month-abbr labels ellipsize to "1…" when ~30 bars share the plot.
 */
export const DENSE_DAILY_AXIS_THRESHOLD = 14;

export type VisitChartBucket = {
  key: string;
  /** Full human label for tooltips / sparse axes ("1 Sept", "Jan 26"). */
  label: string;
  completed: number;
  cancelled: number;
  refunded: number;
};

export function shouldAggregateByMonth(
  preset: DatePreset | null,
  from: string,
  to: string,
): boolean {
  if (preset === 'year') return true;
  if (preset === '7d' || preset === 'month' || preset === 'today' || preset === 'yesterday') {
    return false;
  }
  return inclusiveDaySpan(from, to) > MONTHLY_AGGREGATION_DAY_THRESHOLD;
}

export function isDenseDailyChart(bucketCount: number, byMonth: boolean): boolean {
  return !byMonth && bucketCount > DENSE_DAILY_AXIS_THRESHOLD;
}

/** Axis text: day-of-month when dense daily; otherwise full bucket label. */
export function visitChartAxisLabel(bucket: VisitChartBucket, denseDaily: boolean): string {
  if (denseDaily && /^\d{4}-\d{2}-\d{2}$/.test(bucket.key)) {
    return String(Number(bucket.key.slice(8, 10)));
  }
  return bucket.label.replace(/ /g, '\u00a0');
}

/**
 * Which bars get an X label. Sparse charts (7d / year) label every bar.
 * Dense daily: calendar majors 1, 5, 10, 15, 20, 25… plus first & last.
 */
export function shouldShowVisitChartAxisLabel(
  buckets: VisitChartBucket[],
  index: number,
  denseDaily: boolean,
): boolean {
  if (!denseDaily) return true;
  const n = buckets.length;
  if (index <= 0 || index === n - 1) return true;
  const key = buckets[index]?.key ?? '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const day = Number(key.slice(8, 10));
    return day === 1 || day % 5 === 0;
  }
  const step = Math.max(1, Math.ceil(n / 7));
  return index % step === 0;
}

function visitDayIso(v: TeleconsultManagementVisit): string {
  return String(v.visitDate || '').slice(0, 10);
}

function monthKey(isoDay: string): string {
  return isoDay.slice(0, 7);
}

function formatDayLabel(iso: string): string {
  const { y, m, d } = parseIso(iso);
  return new Date(y, m, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatMonthLabel(yyyyMm: string): string {
  const [ys, ms] = yyyyMm.split('-');
  const y = Number(ys);
  const m = Number(ms) - 1;
  return new Date(y, m, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function emptyBucket(key: string, label: string): VisitChartBucket {
  return { key, label, completed: 0, cancelled: 0, refunded: 0 };
}

function bumpStatus(bucket: VisitChartBucket, status: string): void {
  if (status === 'completed') bucket.completed += 1;
  else if (status === 'cancelled') bucket.cancelled += 1;
  else if (status === 'refunded') bucket.refunded += 1;
}

export function buildVisitChartSeries(
  visits: TeleconsultManagementVisit[],
  from: string,
  to: string,
  preset: DatePreset | null,
): { buckets: VisitChartBucket[]; byMonth: boolean } {
  const byMonth = shouldAggregateByMonth(preset, from, to);

  if (!byMonth) {
    const days = eachIso(from, to);
    const buckets = days.map((iso) => {
      const bucket = emptyBucket(iso, formatDayLabel(iso));
      visits.filter((v) => visitDayIso(v) === iso).forEach((v) => bumpStatus(bucket, v.status));
      return bucket;
    });
    return { buckets, byMonth: false };
  }

  const monthKeys: string[] = [];
  const seen = new Set<string>();
  eachIso(from, to).forEach((iso) => {
    const key = monthKey(iso);
    if (!seen.has(key)) {
      seen.add(key);
      monthKeys.push(key);
    }
  });

  const buckets = monthKeys.map((key) => {
    const bucket = emptyBucket(key, formatMonthLabel(key));
    visits.filter((v) => monthKey(visitDayIso(v)) === key).forEach((v) => bumpStatus(bucket, v.status));
    return bucket;
  });

  return { buckets, byMonth: true };
}

export function visitChartSubtitle(
  preset: DatePreset | null,
  from: string,
  to: string,
  byMonth: boolean,
): string {
  const grain = byMonth ? 'By month' : 'By day';
  if (preset === 'today') return `${grain} · Today — completed / cancelled / refunded`;
  if (preset === 'yesterday') return `${grain} · Yesterday — completed / cancelled / refunded`;
  if (preset === '7d') return `${grain} · Last 7 days — completed / cancelled / refunded`;
  if (preset === 'month') return `${grain} · This month — completed / cancelled / refunded`;
  if (preset === 'year') return `${grain} · This year — completed / cancelled / refunded`;
  const span = inclusiveDaySpan(from, to);
  return `${grain} · ${span} day${span === 1 ? '' : 's'} — completed / cancelled / refunded`;
}
